/**
 * Page maintenance : affichée quand le backend renvoie 503 maintenance.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, RefreshCw } from 'lucide-react';
import { portailApi } from '../services/portailApi';

export default function Maintenance() {
  const navigate = useNavigate();
  const [info, setInfo] = useState<{ message: string; depuis: string | null }>(() => {
    try {
      const s = sessionStorage.getItem('portail_maintenance');
      if (s) return JSON.parse(s);
    } catch { /* ignore */ }
    return { message: '', depuis: null };
  });
  const [checking, setChecking] = useState(false);

  // Polling : vérifier toutes les 30s si la maintenance est terminée
  useEffect(() => {
    const check = async () => {
      try {
        const res = await portailApi.getMaintenanceStatus();
        if (!res.actif) {
          sessionStorage.removeItem('portail_maintenance');
          navigate('/portail/login', { replace: true });
        } else if (res.message !== info.message) {
          setInfo({ message: res.message, depuis: res.depuis });
        }
      } catch { /* ignore */ }
    };
    check();
    const t = setInterval(check, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkNow = async () => {
    setChecking(true);
    try {
      const res = await portailApi.getMaintenanceStatus();
      if (!res.actif) {
        sessionStorage.removeItem('portail_maintenance');
        navigate('/portail/login', { replace: true });
      } else {
        setInfo({ message: res.message, depuis: res.depuis });
      }
    } finally {
      setTimeout(() => setChecking(false), 500);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-gray-900 rounded-2xl shadow-2xl border border-gray-800 p-6 text-center">
        <Wrench size={40} className="mx-auto mb-3 text-amber-400" />
        <h1 className="text-xl font-bold text-amber-300 mb-2">
          Portail en maintenance
        </h1>
        <p className="text-gray-300 text-sm mb-4 whitespace-pre-wrap">
          {info.message || 'Le portail est temporairement indisponible. Nous effectuons des opérations techniques pour vous offrir un meilleur service.'}
        </p>

        {info.depuis && (
          <div className="text-xs text-gray-500 mb-4">
            Maintenance commencée le {new Date(info.depuis).toLocaleString('fr-FR')}
          </div>
        )}

        <div className="bg-gray-800/60 rounded-lg p-3 text-xs text-gray-300 mb-4">
          <div className="font-semibold text-gray-200 mb-1">Que faire ?</div>
          <ul className="text-left space-y-1">
            <li>• Patientez quelques minutes</li>
            <li>• Cette page se rechargera automatiquement</li>
            <li>• Contactez la scolarité si l'attente est longue</li>
          </ul>
        </div>

        <button
          onClick={checkNow}
          disabled={checking}
          className="w-full py-2.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-medium rounded-lg text-sm inline-flex items-center justify-center gap-1.5"
        >
          <RefreshCw size={14} className={checking ? 'animate-spin' : ''} />
          {checking ? 'Vérification...' : 'Vérifier maintenant'}
        </button>

        <div className="mt-4 text-xs text-gray-500">
          Cette page se rafraîchit automatiquement toutes les 30 secondes
        </div>
      </div>
    </div>
  );
}
